import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { ENDPOINT } from "../../../constants/endpointConstants";
import { getToken, formatDateTime, getRangeDates } from "../../../utils/utils";
import Table from "../../../components/Table";

const InsightSalesTable = ({ startDate, endDate }) => {
  const [salesData, setSalesData] = useState({
    sales: [],
    totalSalesAmount: 0,
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy] = useState("saleDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const size = 10;

  const fetchSales = useCallback(async () => {
    const token = getToken();
    setLoading(true);
    try {
      const [dateInitFormatted, dateFinishFormatted] = getRangeDates(
        startDate,
        endDate
      );

      const { data } = await axios.post(
        ENDPOINT.POST_SALES_BY_DATES,
        {
          startDate: formatDateTime(dateInitFormatted),
          endDate: formatDateTime(dateFinishFormatted),
          page,
          size,
          sortBy,
          sortDirection,
        },
        token
      );

      setSalesData({
        sales: data.sales,
        totalSalesAmount: data.totalSalesAmount,
        total: data.total,
      });
    } catch (error) {
      console.error("Error fetching sales:", error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, page, size, sortBy, sortDirection]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSortChange = (newSortBy) => {
    const isSameColumn = newSortBy === sortBy;
    const newSortDirection =
      isSameColumn && sortDirection === "asc" ? "desc" : "asc";
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setPage(1);
  };

  const handleSaleClick = (sale) => {
    setSelectedSale(sale);
    setShowModal(true);
  };

  const columnNames = [
    {
      name: "ID",
      key: "saleId",
      sortKey: "id",
    },
    {
      name: "Cliente",
      key: "clientName",
      sortKey: "clientName",
    },
    {
      name: "Total",
      key: "totalPrice",
      type: "currency",
      sortKey: "totalPrice",
    },
    {
      name: "Descuento",
      key: "discount",
      type: "currency",
      sortKey: "discountAmount",
    },
    {
      name: "Domicilio",
      key: "deliveryPrice",
      type: "currency",
      sortKey: "deliveryPrice",
    },
    {
      name: "Total a Pagar",
      key: "totalPriceToPay",
      type: "currency",
      sortKey: "totalPriceToPay",
    },
    {
      name: "Metodo de Pago",
      key: "payments",
      type: "arrayJoin",
      property: "paymentMethodName",
    },
  ];

  return (
    <div>
      <Table
        columnNames={columnNames}
        items={salesData.sales}
        isLoading={loading}
        itemClick={handleSaleClick}
        pagination={true}
        totalItems={salesData.totalSalesAmount}
        currentPage={page}
        itemsPerPage={size}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
      />

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton className="style-modal-header">
          <Modal.Title className="text-modal">
            Orden #{selectedSale?.saleId} -{" "}
            {selectedSale?.clientName || "Sin nombre"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6>Productos</h6>
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Precio</th>
              </tr>
            </thead>
            <tbody>
              {selectedSale?.saleProducts?.length ? (
                selectedSale.saleProducts.map((product, index) => (
                  <tr key={index}>
                    <td>{product.productName}</td>
                    <td>
                      $
                      {new Intl.NumberFormat("es-CL").format(
                        product.productPrice
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2}>Sin productos</td>
                </tr>
              )}
            </tbody>
          </table>

          <h6 className="mt-4">Domicilio</h6>
          <span>
            $
            {new Intl.NumberFormat("es-CL").format(
              selectedSale?.deliveryPrice || 0
            )}
          </span>

          <h6 className="mt-4">Descuento</h6>
          <span>
            $
            {new Intl.NumberFormat("es-CL").format(selectedSale?.discount || 0)}
          </span>

          <h6 className="mt-4">Pagos</h6>
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Metodo de pago</th>
                <th>Monto</th>
              </tr>
            </thead>
            <tbody>
              {selectedSale?.payments?.length ? (
                selectedSale.payments.map((payment, index) => (
                  <tr key={index}>
                    <td>{payment.paymentMethodName}</td>
                    <td>
                      $
                      {new Intl.NumberFormat("es-CL").format(
                        (payment.amount || 0) + (payment.deliveryAmount || 0)
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2}>Sin pagos</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="mt-4 d-flex justify-content-between">
            <strong>Total a Pagar:</strong>
            <strong>
              $
              {new Intl.NumberFormat("es-CL").format(
                selectedSale?.totalPriceToPay || 0
              )}
            </strong>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default InsightSalesTable;
